import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Auth,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  user,
  User,
  onIdTokenChanged,
  updatePassword,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
} from '@angular/fire/auth';
import { setPersistence } from 'firebase/auth';
import {
  BehaviorSubject,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$: Observable<User | null>;
  private claimsSubject = new BehaviorSubject<any>(null);
  claims$ = this.claimsSubject.asObservable();
  isSocialAccount$: Observable<boolean>;
  isOnboardingCompleted = false;
  isEmailVerified$: Observable<boolean | null> = of(null);
  hub_id = new BehaviorSubject<number>(0);
  private _auth = inject(Auth);

  constructor(
    private _http: HttpClient,
  ) {
    this.setSessionStoragePersistence();
    this.user$ = user(this._auth);
    this.isSocialAccount$ = this.user$.pipe(
      map((user) => {
        if (!user) return false;
        return user.providerData.some(
          (provider) => provider.providerId !== 'password',
        );
      }),
    );
    this.isEmailVerified$ = this.user$.pipe(
      map((user) => {
        if (!user) return false;
        return user.emailVerified;
      }),
    );

    this.claims$.subscribe((claims) => {
      this.isOnboardingCompleted = claims?.hasOwnProperty('user') ?? false;
    });

    onIdTokenChanged(this._auth, (user) => {
      if (user) {
        this.refreshClaims();
      } else {
        this.claimsSubject.next(null);
      }
    });

    this.user$
      .pipe(
        switchMap((user) => {
          if (!user) {
            return of(null);
          }
          return from(user.getIdTokenResult()).pipe(
            map((tokenResult) => tokenResult.claims),
          );
        }),
      )
      .subscribe((claims) => {
        this.claimsSubject.next(claims);
      });
  }

  refreshClaims(): Promise<any> {
    const user = this._auth.currentUser;
    if (!user) return Promise.resolve(null);

    return user.getIdTokenResult(true).then((tokenResult) => {
      this.claimsSubject.next(tokenResult.claims);
      return tokenResult.claims;
    });
  }

  getUser(): User | null {
    const user = this._auth.currentUser;
    if (!user) return null;

    return user;
  }

  private setSessionStoragePersistence(): void {
    setPersistence(this._auth, browserLocalPersistence);
  }

  login(email: string, password: string): Observable<void> {
    const promise = signInWithEmailAndPassword(
      this._auth,
      email,
      password,
    ).then(() => {
      this.refreshClaims();
    });
    return from(promise);
  }

  performPasswordReset(email: string): Observable<void> {
    const promise = sendPasswordResetEmail(this._auth, email).then(() => {
      this.refreshClaims();
    });
    return from(promise);
  }

  changePassword(newPassword: string): Observable<void> {
    const user = this._auth.currentUser;
    if (!user) {
      return of();
    }

    const promise = updatePassword(user, newPassword).then(() => {
      this.refreshClaims();
    });
    return from(promise);
  }

  performPasswordResetWithCode(
    oobCode: string,
    newPassword: string,
  ): Observable<void> {
    const promise = verifyPasswordResetCode(this._auth, oobCode)
      .then(() => {
        return confirmPasswordReset(this._auth, oobCode, newPassword);
      })
      .then(() => {
        this.refreshClaims();
      });
    return from(promise);
  }

  register(email: string, password: string): Observable<void> {
    const promise = createUserWithEmailAndPassword(
      this._auth,
      email,
      password,
    ).then(async (user) => {
      await this.createUserDoc();
      await this.sendEmailVerification();
    });
    return from(promise);
  }

  async createUserDoc() {
    const result = await firstValueFrom(
      this._http.put<any>(`${environment.apiUrl}/regional/users`, {}),
    );
  }
  logout(): Observable<void> {
    const promise = signOut(this._auth).then(() => {
      sessionStorage.clear();
      this.claimsSubject.next(null);
    });
    return from(promise);
  }

  performEmailVerification(oobCode: string): Observable<void> {
    const promise = verifyPasswordResetCode(this._auth, oobCode)
      .then(() => {
        return applyActionCode(this._auth, oobCode);
      })
      .then(() => {
        this.isEmailVerified$ = of(true);
      });
    return from(promise);
  }

  sendEmailVerification() {
    return firstValueFrom(
      this._http.post(`${environment.apiUrl}/regional/users/email/verify`, {}),
    );
  }

  performGoogleLogin(): Observable<void> {
    const provider = new GoogleAuthProvider();
    const promise = signInWithPopup(this._auth, provider)
      .then(async (result) => {
        // Refresh claims after Google login
        this.refreshClaims();
        await this.createUserDoc();
      })
      .catch((error) => {
        // Handle Errors here.
      });
    return from(promise);
  }
  getClaim(key: string): Observable<any> {
    return this.claims$.pipe(
      map((claims) => {
        if (!claims) return null;
        return claims[key];
      }),
    );
  }

  hasClaim(claim: string[]): Observable<boolean> {
    return this.claims$.pipe(
      map((claims) => {
        if (!claims) return false;
        return claim.some((c) => claims.hasOwnProperty(c));
      }),
    );
  }
}